import React from 'react';
import { Link } from 'react-router-dom';
import { Home as HomeIcon, Facebook, Instagram, Linkedin } from 'lucide-react';
import { track } from '../lib/analytics';

/**
 * Social links — central config. To enable a new channel, set `url` to a non-empty string.
 * Channels with url='' are hidden automatically until ready.
 */
const SOCIAL_LINKS = [
  {
    name: 'Instagram',
    handle: '@geth.abita',
    url: 'https://www.instagram.com/geth.abita',
    Icon: Instagram,
  },
  {
    name: 'Facebook',
    handle: 'habita.co',
    url: 'https://www.facebook.com/share/1Ehx2532Br/',
    Icon: Facebook,
  },
  {
    name: 'TikTok',
    handle: '@gethabita',
    url: '', // TODO: set when channel is live
    // TikTok icon — Lucide doesn't have one yet, use inline SVG
    Icon: ({ className }) => (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52V6.89a4.85 4.85 0 0 1-1.84-.2z"/>
      </svg>
    ),
  },
  {
    name: 'LinkedIn',
    handle: 'habita',
    url: '', // TODO: set when channel is live
    Icon: Linkedin,
  },
];

export default function Footer() {
  const visibleSocials = SOCIAL_LINKS.filter((s) => s.url);

  return (
    <footer className="bg-[#02543A] text-white mt-20">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-md bg-white flex items-center justify-center">
                <HomeIcon className="w-5 h-5 text-habita" strokeWidth={2.5} />
              </div>
              <span className="font-serif font-bold text-2xl">habita<span className="text-amber-400">.</span>co</span>
            </div>
            <p className="text-sm text-white/70 max-w-sm leading-relaxed">
              La plataforma inmobiliaria más completa de Colombia. Compra, arrienda y publica propiedades con la confianza de los expertos.
            </p>
            {visibleSocials.length > 0 && (
              <div className="flex items-center gap-3 mt-5" data-testid="footer-socials">
                {visibleSocials.map(({ name, url, Icon }) => (
                  <a
                    key={name}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Síguenos en ${name}`}
                    title={`Síguenos en ${name}`}
                    data-testid={`social-${name.toLowerCase()}`}
                    onClick={() => { try { track('social_click', { channel: name.toLowerCase() }); } catch {} }}
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-amber-400 hover:text-habita-dark text-white transition-all duration-200 flex items-center justify-center hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-400/20"
                  >
                    <Icon className="w-[18px] h-[18px]" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-sm">Explora</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/buscar?op=venta" className="hover:text-white">Comprar</Link></li>
              <li><Link to="/buscar?op=arriendo" className="hover:text-white">Arrendar</Link></li>
              <li><Link to="/publicar" className="hover:text-white">Vender</Link></li>
              <li><Link to="/calculadora" className="hover:text-white">Calculadora hipoteca</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-sm">Empresa</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/sobre-nosotros" className="hover:text-white">Sobre nosotros</Link></li>
              <li><Link to="/prensa" className="hover:text-white">Prensa</Link></li>
              <li><Link to="/contacto" className="hover:text-white">Contacto</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-sm">Ayuda</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/contacto" className="hover:text-white">Centro de ayuda</Link></li>
              <li><Link to="/terminos" className="hover:text-white" data-testid="footer-link-terminos">Términos y Condiciones</Link></li>
              <li><Link to="/privacidad" className="hover:text-white" data-testid="footer-link-privacidad">Política de Privacidad</Link></li>
              <li><Link to="/politica-datos" className="hover:text-white" data-testid="footer-link-politica-datos">Tratamiento de Datos · Ley 1581</Link></li>
              <li><Link to="/cookies" className="hover:text-white" data-testid="footer-link-cookies">Política de Cookies</Link></li>
              <li><Link to="/habeas-data" className="hover:text-white" data-testid="footer-link-habeas">Habeas Data</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/15 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/60">© 2026 habita.co Todos los derechos reservados</p>
          <p className="text-xs text-white/60">
            <a href="mailto:contacto@gethabita.co" className="hover:text-white">contacto@gethabita.co</a>
            <span className="mx-2 text-white/30">·</span>
            Hecho con cariño en Colombia
          </p>
        </div>
      </div>
    </footer>
  );
}
